import React, { useCallback, useMemo } from 'react';
import { Container, Sprite } from '@pixi/react';
import { observer } from 'mobx-react-lite';
import * as PIXI from 'pixi.js';

import { gameStore } from '../../../../../store/gameStore';
import { jackPotStore } from '../../../../../store/jackPotStore';
import { userInfoStore } from '../../../../../store/userInfoStore';
import { bettingPartStore } from '../../../../../store/bettingPartStore';
import { applicationStore } from '../../../../../store/applicationStore';

import { isMobile } from '../../../../../core/helpers/isMobile';
import { isoStandard } from '../../../../../core/helpers/isoStandard';
import { getGameImages } from '../../../../../core/helpers/getGameImages';
import { parseNumberByPrecision } from '../../../../../core/helpers/parseNumberByPrecision';

import ScrollBox from '../../../../web/scrollBox';
import PayTableStepTitle from '../payTableStepTitle';
import PayTableSymbolsOdds from '../payTableSymbolsOdds';
import PayTableStepInfoText from '../payTableStepInfoText';

const oddsInfoEnumMobile = [
  { id: 0, oddX: 12, oddContainerY: 15, oddsContainerX: 70, oddsContainerY: 598 },
  { id: 1, oddX: 12, oddContainerY: 15, oddsContainerX: 210, oddsContainerY: 517 },
  { id: 2, oddX: 12, oddContainerY: 15, oddsContainerX: 325, oddsContainerY: 517 },
  { id: 3, oddX: 12, oddContainerY: 15, oddsContainerX: 438, oddsContainerY: 517 },
  { id: 4, oddX: 12, oddContainerY: 15, oddsContainerX: 210, oddsContainerY: 649 },
  { id: 5, oddX: 12, oddContainerY: 15, oddsContainerX: 295, oddsContainerY: 649 },
  { id: 6, oddX: 12, oddContainerY: 15, oddsContainerX: 380, oddsContainerY: 649 },
  { id: 7, oddX: 12, oddContainerY: 15, oddsContainerX: 465, oddsContainerY: 649 },
  { id: 8, oddX: 12, oddContainerY: 15, oddsContainerX: 410, oddsContainerY: 233 },
  { id: 9, oddX: 12, oddContainerY: 15, oddsContainerX: 240, oddsContainerY: 233 },
];

const PayTableContent = observer(({ payTableContentWidth }) => {
  const {
    canvasTranslationKeys: {
      WILD,
      JACKPOT,
      WILDS_TEXT,
      WINNING_LINES,
      JACKPOT_INTRO,
      SYMBOL_PAYOUT,
      SYMBOL_PAYOUT_1,
      SYMBOL_PAYOUT_2,
      SYMBOL_PAYOUT_3,
      WINNING_LINES_TEXT,
    },
  } = applicationStore;
  const { oddsInfo } = gameStore;
  const { hasJackPot } = jackPotStore;
  const { currencyIcon, currencySymbol, precision } = userInfoStore;
  const { selectedQuickBet } = bettingPartStore;
  const { isShowLicenseMode, isDemo } = applicationStore;

  const {
    payTableTitleLeft,
    payTableTitleRight,
    payTableStep0Image,
    payTableStep1Image,
    payTableStep2Image,
    payTableStep3Image,
    payTableStep4Image,
  } = useMemo(() => {
    return getGameImages();
  }, []);

  const payTableStepsContent = useMemo(() => {
    return {
      step0: {
        id: 0,
        text: SYMBOL_PAYOUT,
        contentImage: payTableStep0Image,
        stepInfoText: [
          {
            x: isMobile() ? 22 : 255,
            y: isMobile() ? 300 : 258,
            type: 'middleText',
            align: 'center',
            wordWrapWidth: isMobile() ? 160 : 280,
            text: SYMBOL_PAYOUT_1,
          },
          {
            x: isMobile() ? 197 : 587,
            y: isMobile() ? 300 : 258,
            type: 'middleText',
            align: 'center',
            wordWrapWidth: isMobile() ? 160 : 280,
            text: SYMBOL_PAYOUT_2,
          },
          {
            x: isMobile() ? 362 : 935,
            y: isMobile() ? 300 : 258,
            type: 'middleText',
            align: 'center',
            wordWrapWidth: isMobile() ? 160 : 280,
            text: SYMBOL_PAYOUT_3,
          },
        ],
        y: 0,
      },
      step1: {
        id: 1,
        text: WINNING_LINES,
        contentImage: payTableStep1Image,
        stepInfoText: [
          {
            x: isMobile() ? 270 : 2160,
            y: isMobile() ? 2225 : 675,
            anchor: 0.5,
            type: 'text',
            align: 'center',
            wordWrapWidth: isMobile() ? 394 : 687,
            text: WINNING_LINES_TEXT,
          },
        ],
        y: 717,
      },
      step2: {
        isHide: isMobile(),
        id: 2,
        text: WINNING_LINES,
        contentImage: payTableStep2Image,
        stepInfoText: [
          {
            x: isMobile() ? 270 : 3600,
            y: isMobile() ? 1270 : 675,
            anchor: 0.5,
            type: 'text',
            align: 'center',
            wordWrapWidth: isMobile() ? 394 : 687,
            text: WINNING_LINES_TEXT,
          },
        ],
        y: isMobile() ? 1340 : 1469,
      },
      step3: {
        id: 3,
        text: WILD,
        contentImage: payTableStep3Image,
        stepInfoText: [
          {
            x: isMobile() ? 270 : 5040,
            y: isMobile() ? 2705 : 690,
            anchor: 0.5,
            type: 'text',
            align: 'center',
            wordWrapWidth: isMobile() ? 394 : 687,
            text: WILDS_TEXT,
          },
        ],
        y: isMobile() ? 2290 : 2024,
      },
      step4: {
        isHide: !hasJackPot,
        id: 4,
        text: JACKPOT,
        contentImage: payTableStep4Image,
        stepInfoText: [
          {
            x: isMobile() ? 270 : 6480,
            y: isMobile() ? 3165 : 675,
            anchor: 0.5,
            type: 'text',
            align: 'center',
            wordWrapWidth: isMobile() ? 394 : 687,
            text: JACKPOT_INTRO,
          },
        ],
        y: isMobile() ? 2765 : 2600,
      },
    };
  }, [
    WILD,
    JACKPOT,
    WILDS_TEXT,
    SYMBOL_PAYOUT,
    JACKPOT_INTRO,
    WINNING_LINES,
    SYMBOL_PAYOUT_1,
    SYMBOL_PAYOUT_2,
    SYMBOL_PAYOUT_3,
    WINNING_LINES_TEXT,
    hasJackPot,
    payTableStep0Image,
    payTableStep2Image,
    payTableStep1Image,
    payTableStep3Image,
    payTableStep4Image,
  ]);

  const handlePayTableInfoTextStyles = useCallback((type, align, wordWrapWidth) => {
    const mainStyles = { align: align, fill: '#FFFFFF', fontFamily: 'poppinsMedium' };
    if (type === 'title') {
      return { ...mainStyles, fontSize: 30, fontWeight: 700 };
    }
    if (type === 'subTitle') {
      return { ...mainStyles, fontSize: 14, fontWeight: 400 };
    }
    if (type === 'middleText') {
      return {
        ...mainStyles,
        fontSize: 12,
        fontWeight: 500,
        wordWrap: true,
        wordWrapWidth: wordWrapWidth,
      };
    }
    return {
      ...mainStyles,
      fontSize: 14,
      fontWeight: 400,
      wordWrap: true,
      wordWrapWidth: wordWrapWidth,
    };
  }, []);

  const handlePayTableOddsInfoStyles = useCallback(() => {
    return {
      fontSize: 12,
      fontWeight: 500,
      fontFamily: 'poppinsMedium',
    };
  }, []);

  const handlePayTableTitleContainer = useCallback(
    (step, parent) => {
      const payTableTitleContainer = new PIXI.Container('PayTableTitleContainer');
      const titleText = new PIXI.Text(step.text, {
        anchor: 0.5,
        resolution: 2,
        x: 270,
        fontSize: 30,
        fontWeight: 700,
        fill: '#FFFFFF',
        fontFamily: 'poppinsMedium',
      });
      titleText.x = 270;
      titleText.anchor.set(0.5);
      titleText.resolution = 2;

      const height = step.y ? step.y + titleText.height / 2 : step.y + titleText.height / 2;

      payTableTitleContainer.y = height;

      const payTableTitleRightTexture = PIXI.Sprite.from(payTableTitleRight);
      payTableTitleRightTexture.x = 270 - titleText.width / 2 - 10;
      payTableTitleRightTexture.anchor = { y: 0.5, x: 1 };

      const payTableTitleLeftTexture = PIXI.Sprite.from(payTableTitleLeft);
      payTableTitleLeftTexture.x = 270 + titleText.width / 2 + 10;
      payTableTitleLeftTexture.anchor = { y: 0.5, x: 0 };
      payTableTitleContainer.addChild(payTableTitleRightTexture);
      payTableTitleContainer.addChild(titleText);
      payTableTitleContainer.addChild(payTableTitleLeftTexture);
      parent.addChild(payTableTitleContainer);
    },
    [payTableTitleLeft, payTableTitleRight]
  );

  const handlePayTableContentImage = useCallback((step, parent) => {
    const contentImage = PIXI.Sprite.from(step.contentImage);
    contentImage.y = step.y;
    parent.addChild(contentImage);
  }, []);

  const handlePayTableStepTextContainer = useCallback(
    (step, parent) => {
      const payTableTextContainer = new PIXI.Container('PayTableTextContainer');

      step.stepInfoText.forEach((item) => {
        const payTableStepText = new PIXI.Text(item.text, {
          x: item.x,
          y: item.y,
          resolution: 2,
          anchor: item.anchor,
          ...handlePayTableInfoTextStyles(item.type, item.align, item.wordWrapWidth),
        });
        payTableStepText.x = item.x;
        payTableStepText.y = item.y;
        payTableStepText.resolution = 2;
        payTableStepText.anchor.set(item.anchor);
        payTableTextContainer.addChild(payTableStepText);
      });
      parent.addChild(payTableTextContainer);
    },
    [handlePayTableInfoTextStyles]
  );

  const handlePayTableOddsContainer = useCallback(
    (step, parent) => {
      if (step.id === 0) {
        const payTableOddsContainer = new PIXI.Container('PayTableOddsContainer');

        oddsInfoEnumMobile.map((item, index) => {
          const payTableSymbolsOddsContainer = new PIXI.Container('PayTableSymbolsOddsContainer');
          payTableSymbolsOddsContainer.x = item.oddsContainerX;
          payTableSymbolsOddsContainer.y = item.oddsContainerY;
          oddsInfo[index]?.map((odd, oddIndex) => {
            const payTableSymbolOddContainer = new PIXI.Container('PayTableSymbolOddContainer');
            const payTableSymbolOddRowContainer = new PIXI.Container(
              'PayTableSymbolOddRowContainer'
            );
            payTableSymbolOddContainer.y = oddIndex * item.oddContainerY;

            const itemCount = new PIXI.Text(
              item.id === 9 ? `${3 - oddIndex}x` : `${5 - oddIndex}x`,
              {
                ...handlePayTableOddsInfoStyles(),
                fill: '#FFFFFF',
              }
            );
            itemCount.resolution = 2;
            itemCount.anchor = { x: 1, y: 0.5 };

            const formattedWinAmountByItemCount = `${
              isShowLicenseMode && !isDemo ? currencyIcon : ''
            }${isoStandard(parseNumberByPrecision(odd, selectedQuickBet, precision))}${
              isShowLicenseMode && isDemo ? currencySymbol : ''
            }`;

            const winAmountByItemCount = new PIXI.Text(formattedWinAmountByItemCount, {
              ...handlePayTableOddsInfoStyles(),
              fill: '#FFC33E',
            });
            winAmountByItemCount.x = item.oddX;
            winAmountByItemCount.resolution = 2;
            winAmountByItemCount.anchor = { x: 0, y: 0.5 };
            payTableSymbolOddRowContainer.addChild(itemCount);
            payTableSymbolOddRowContainer.addChild(winAmountByItemCount);
            payTableSymbolOddContainer.addChild(payTableSymbolOddRowContainer);
            payTableSymbolsOddsContainer.addChild(payTableSymbolOddContainer);
            return payTableSymbolsOddsContainer;
          });
          payTableOddsContainer.addChild(payTableSymbolsOddsContainer);
          return payTableOddsContainer;
        });
        parent.addChild(payTableOddsContainer);
      }
    },
    [oddsInfo, precision, selectedQuickBet, handlePayTableOddsInfoStyles]
  );

  const payTableContentContainer = useMemo(() => {
    return new PIXI.Container('PayTableContentContainer');
  }, []);

  const handlePayTableScrollBoxContent = useCallback(() => {
    payTableContentContainer.children.length = 0;
    return Object.values(payTableStepsContent)
      .filter((step) => !step.isHide)
      .map((step) => {
        handlePayTableContentImage(step, payTableContentContainer);
        handlePayTableTitleContainer(step, payTableContentContainer);
        handlePayTableStepTextContainer(step, payTableContentContainer);
        handlePayTableOddsContainer(step, payTableContentContainer);

        return payTableContentContainer;
      });
  }, [
    payTableStepsContent,
    payTableContentContainer,
    handlePayTableContentImage,
    handlePayTableOddsContainer,
    handlePayTableTitleContainer,
    handlePayTableStepTextContainer,
  ]);

  return (
    <Container>
      {isMobile() ? (
        <ScrollBox
          y={125}
          boxWidth={540}
          boxHeight={775}
          scrollHeight={775}
          isFromGameRules={false}
          content={handlePayTableScrollBoxContent()}
          scrollBarX={525}
          scrollBarY={-5}
          maxScrollBarY={120}
        />
      ) : (
        Object.values(payTableStepsContent)
          .filter((step) => !step.isHide)
          .map((step) => {
            return (
              <Container key={step.id} name='PayTableContentContainer'>
                <Sprite
                  x={isMobile() ? 0 : step.id * payTableContentWidth}
                  y={isMobile() ? step.y : 0}
                  texture={step.contentImage}
                />
                <PayTableStepTitle text={step.text} stepId={step.id} y={step.y} />
                {step.stepInfoText.map((item) => {
                  return (
                    <PayTableStepInfoText
                      x={item.x || 0}
                      y={item.y || 0}
                      key={item.text}
                      text={item.text}
                      type={item.type}
                      align={item.align}
                      anchor={item.anchor}
                      wordWrapWidth={item.wordWrapWidth}
                    />
                  );
                })}
                {!step.id && <PayTableSymbolsOdds />}
              </Container>
            );
          })
      )}
    </Container>
  );
});

export default PayTableContent;
